// Vendors Intelligence Workspace — portfolio export modal (Slice 4).
// Two scopes: Portfolio summary (server-enforced aggregate-only · shareable) · Full workspace
// (named + watermarked · internal). Format CSV/XLSX (summary) or XLSX (full). Inherits range + filters
// — ⚠ the raw search term is sanitized SERVER-side (the client passes it; the server omits it).
import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { downloadPortfolioExport } from '@/api/hooks/useVendors';

function ScopeOption({ active, onClick, title, badge, desc }: {
  active: boolean; onClick: () => void; title: string; badge: string; desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border p-3 flex flex-col gap-1 transition-colors ${active ? 'border-accent-cyan/60 bg-accent-cyan/5' : 'border-border-subtle hover:bg-bg-surface-2'}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-text-primary">{title}</span>
        <span className="text-[10px] uppercase tracking-wide text-text-faint border border-border-subtle rounded px-1.5 py-0.5">{badge}</span>
      </div>
      <span className="text-[11.5px] text-text-secondary leading-relaxed">{desc}</span>
    </button>
  );
}

export function PortfolioExportModal({ open, onClose, currentParams, range }: {
  open: boolean; onClose: () => void; currentParams: URLSearchParams; range: string;
}) {
  const [scope, setScope] = useState<'summary' | 'full'>('summary');
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      await downloadPortfolioExport(scope, format, currentParams, range);
      onClose();
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Export vendor portfolio</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-4">
          <p className="text-xs text-text-secondary">Inherits the current date range ({range}) and filters. The search term is never included in the summary export.</p>
          <div className="flex flex-col gap-2">
            <ScopeOption
              active={scope === 'summary'} onClick={() => setScope('summary')}
              title="Portfolio summary" badge="No vendor names"
              desc="KPIs, trends and distributions only — aggregate counts and rates. Safe to share outside the GRC team."
            />
            <ScopeOption
              active={scope === 'full'} onClick={() => setScope('full')}
              title="Full workspace" badge="Names included"
              desc="Everything above plus the attention list and both vendor tables, with names. Internal use — watermarked with your name and the time."
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-secondary">Format</span>
            {(['csv', 'xlsx'] as const).map((f) => (
              <button
                key={f} type="button" onClick={() => setFormat(f)}
                className={`text-xs font-semibold rounded-md px-2.5 py-1 border transition-colors ${format === f ? 'bg-accent-cyan/15 border-accent-cyan/50 text-accent-cyan' : 'border-border-subtle text-text-secondary hover:text-text-primary'}`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={run} disabled={busy}>{busy ? 'Exporting…' : 'Export'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
